import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertIcon,
    AlertTitle,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    useTheme,
    useToast
} from '@chakra-ui/react';
import { type Theme } from '@emotion/react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useClientApiFetch } from 'ts/client/common/hooks';
import DataConsentCheckbox from 'ts/common/components/DataConsentCheckbox';
import {
    getFullPaymentAmount,
    getPartialPaymentAmount,
    isAutoPaySetupAvailable
} from 'ts/common/components/invoice/helpers';
import { getTranslationKeyForStripeError } from 'ts/common/components/payments/StripeStandard/common';
import { FetchContentType, FetchMethod, useTranslate } from 'ts/common/hooks';
import { useBootstrapData } from 'client_react/bootstrap';
import ClientPaymentForm from 'client_react/common/components/ClientPaymentForm';
import { type ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import { getStripeAppearance, getStripeFonts } from 'client_react/common/helpers';
import InvoiceAutoPaySetUp from 'client_react/invoice/components/InvoiceAutoPaySetUp';
import InvoicePaymentOptions from 'client_react/invoice/components/InvoicePaymentOptions';
import InvoicePaymentSummary from 'client_react/invoice/components/InvoicePaymentSummary';
import InvoiceTipSection from 'client_react/invoice/components/InvoiceTipSection';

export interface FormFields {
    name: string;
    email: string;
    note?: string;
    tipAmount?: number;
    setUpAutoPay?: boolean;
    doesAcceptTerms?: boolean;
}

interface PaymentResponse {
    info?: {
        transactionErrors?: {
            unexpected?: string;
        };
    };
    type: string;
}

interface PaymentSetupIntentResponse {
    setupIntentId: string;
}

interface Props {
    customAmount: number;
    paymentAmountType: ClientPaymentOption;
    setCustomAmount: (amount: number) => void;
    setPaymentAmountType: (x: ClientPaymentOption) => void;
}

const StripeInvoicePaymentFormContent = ({
    customAmount,
    paymentAmountType,
    setCustomAmount,
    setPaymentAmountType
}: Props) => {
    const navigate = useNavigate();
    const toast = useToast();
    const t = useTranslate('clientInvoice');
    const { isSubjectToGdpr } = useBootstrapData();

    const { invoice, refetchInvoice } =
        useOutletContext<{ invoice: SpApi.Client.IInvoice; refetchInvoice: () => Promise<void> }>();

    const form = useForm<FormFields>({
        defaultValues: {
            tipAmount: 0
        },
        mode: 'onTouched'
    });

    const stripe = useStripe();
    const elements = useElements();

    const [isReadyToPay, setIsReadyToPay] = useState(false);
    const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);

    const handlePaymentFormReady = useCallback(() => {
        setIsReadyToPay(true);
    }, []);

    const handleClickBack = useCallback(() => {
        navigate(`/invoice/${invoice.publicId}`);
    }, [invoice, navigate]);

    const { performFetch: createInvoicePayment } = useClientApiFetch<PaymentResponse>(
        `brand/${invoice.brand.id}/invoice/${invoice.publicId}/payment`,
        {
            defer: true,
            headers: {
                'Content-Type': FetchContentType.SpJson
            },
            method: FetchMethod.POST
        }
    );

    const { performFetch: createSetupIntent } = useClientApiFetch<PaymentSetupIntentResponse>(
        `brand/${invoice.brand.id}/invoice/${invoice.publicId}/payment-setup-intent`,
        {
            defer: true,
            headers: {
                'Content-Type': FetchContentType.SpJson
            },
            method: FetchMethod.POST
        }
    );

    const { performFetch: addInvoiceCreditCard } = useClientApiFetch(
        `brand/${invoice.brand.id}/invoice/${invoice.publicId}/credit-card`,
        {
            defer: true,
            headers: {
                'Content-Type': FetchContentType.SpJson
            },
            method: FetchMethod.PUT
        }
    );

    useEffect(() => {
        if (paymentAmountType) {
            form.setValue('tipAmount', 0);
        }
    }, [paymentAmountType, form]);

    if (!stripe || !elements) {
        return null;
    }

    const handleSubmit = async (formData: FormFields) => {
        const { email, name, note, setUpAutoPay, tipAmount } = formData;

        setErrorMessage(null);

        const { error: submitError } = await elements.submit();

        if (submitError) {
            setErrorMessage(submitError.message || t('error.cardDeclined'));
            return;
        }

        if (setUpAutoPay) {
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                elements,
                params: {
                    billing_details: {
                        email,
                        name
                    }
                }
            });

            if (error) {
                setErrorMessage(error.message || t('setUpAutoPayFailed'));
                return;
            }

            const setupIntentResponse = await createSetupIntent({
                body: JSON.stringify({
                    paymentMethodId: paymentMethod.id,
                    nameOnCard: name,
                    emailAddress: email
                })
            });

            if (!setupIntentResponse || setupIntentResponse.status >= 400) {
                setErrorMessage(t('setUpAutoPayFailed'));
                return;
            }

            const creditCardResponse = await addInvoiceCreditCard({
                body: JSON.stringify({
                    billingPostalCode: paymentMethod.billing_details?.address?.postal_code,
                    cardNumber: paymentMethod.card?.last4,
                    emailAddress: email,
                    enableAutoPay: true,
                    expirationMonth: paymentMethod.card?.exp_month,
                    expirationYear: paymentMethod.card?.exp_year,
                    invoiceId: invoice.publicId,
                    nameOnCard: name,
                    setupIntentId: setupIntentResponse.setupIntentId,
                    type: 'credit-card'
                })
            });

            if (!creditCardResponse || creditCardResponse.status >= 400) {
                setErrorMessage(t('setUpAutoPayFailed'));
                return;
            }
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
            elements,
            params: {
                billing_details: {
                    email,
                    name
                }
            }
        });

        if (error) {
            setErrorMessage(error.message || t('error.cardDeclined'));
            return;
        }

        const creditCard = {
            cardNumber: paymentMethod.card?.last4,
            emailAddress: email,
            enableAutoPay: false,
            expirationMonth: paymentMethod.card?.exp_month,
            expirationYear: paymentMethod.card?.exp_year,
            type: 'credit-card'
        };

        const amount =
            paymentAmountType === 'full'
                ? getFullPaymentAmount(invoice)
                : paymentAmountType === 'retainer'
                ? getPartialPaymentAmount(invoice)
                : customAmount;

        let invoicePaymentResponse = await createInvoicePayment({
            body: JSON.stringify({
                amount,
                tipAmount,
                creditCard,
                invoiceId: invoice.publicId,
                note,
                paymentMethodId: paymentMethod.id,
                type: 'invoice-payment'
            })
        });

        // This payment intent requires user verification (3D Secure, etc.)
        // So we need to handle that and send the confirmed payment intent id to the process endpoint.
        if (
            invoicePaymentResponse?.info?.transactionErrors?.unexpected?.includes('requires_action')
        ) {
            // Currently /process returns msg that looks like this
            // requires_action:pi_3OekzwPM8cgEUii90tw9o0vH_secret_308azesV6ORXRE5gIKMxfuGBa
            const arrayOfMsg = invoicePaymentResponse.info.transactionErrors.unexpected.split(':');

            if (!arrayOfMsg[1].startsWith('pi_')) {
                setErrorMessage(t('error.cardDeclined'));
                return;
            }

            const { error, paymentIntent } = await stripe.handleNextAction({
                clientSecret: arrayOfMsg[1]
            });

            if (error || paymentIntent?.status !== 'succeeded') {
                setErrorMessage(error?.message || t('error.cardDeclined'));
                return;
            }

            invoicePaymentResponse = await createInvoicePayment({
                body: JSON.stringify({
                    amount,
                    tipAmount,
                    creditCard,
                    invoiceId: invoice.publicId,
                    paymentMethodId: paymentMethod.id,
                    paymentIntentId: paymentIntent.id,
                    type: 'invoice-payment'
                })
            });
        }

        if (!invoicePaymentResponse || invoicePaymentResponse.status >= 400) {
            const errorMessage = invoicePaymentResponse?.info?.transactionErrors?.unexpected
                ? t(
                      `error.${getTranslationKeyForStripeError(
                          invoicePaymentResponse.info.transactionErrors.unexpected
                      )}`
                  )
                : t('error.unexpectedError');

            setErrorMessage(errorMessage);

            return;
        }

        await refetchInvoice();

        navigate(`/invoice/${invoice.publicId}`);

        toast({
            title: t('paymentSuccess'),
            status: 'success'
        });
    };

    const totalPaymentAmount =
        paymentAmountType === 'full'
            ? getFullPaymentAmount(invoice)
            : paymentAmountType === 'retainer'
            ? getPartialPaymentAmount(invoice)
            : customAmount;

    const showTipSection =
        invoice.isTippingEnabled &&
        (paymentAmountType === 'full' || !invoice.isTipOnlyOnFinalPayment);

    const showAutoPaySetup =
        paymentAmountType === 'retainer' && isAutoPaySetupAvailable(invoice);

    const maxPaymentAmount = getFullPaymentAmount(invoice);
    const isCustomAmountValid =
        paymentAmountType !== 'custom' || (customAmount >= 1 && customAmount <= maxPaymentAmount);

    return (
        <FormProvider {...form}>
            <ClientPaymentForm
                backButtonText={t('back')}
                billingInformationFields={
                    <>
                        <FormControl isRequired paddingBottom={0}>
                            <FormLabel>{t('name')}</FormLabel>
                            <Input {...form.register('name', { required: true })} />
                        </FormControl>
                        <PaymentElement onReady={handlePaymentFormReady} />
                        <FormControl isRequired paddingBottom={0}>
                            <FormLabel>{t('email')}</FormLabel>
                            <Input type="email" {...form.register('email', { required: true })} />
                        </FormControl>
                        <FormControl paddingBottom={0}>
                            <FormLabel>{t('note')}</FormLabel>
                            <Textarea {...form.register('note')} />
                        </FormControl>
                        {showAutoPaySetup && (
                            <InvoiceAutoPaySetUp translate={t} {...form.register('setUpAutoPay')} />
                        )}
                        {isSubjectToGdpr && (
                            <DataConsentCheckbox register={form.register} translate={t} />
                        )}
                        {errorMessage && (
                            <Alert status="error">
                                <AlertIcon />
                                <AlertTitle>{errorMessage}</AlertTitle>
                            </Alert>
                        )}
                    </>
                }
                billingInformationTitle={t('billingInformation')}
                isSubmitDisabled={
                    !stripe ||
                    !elements ||
                    !isReadyToPay ||
                    form.formState.isSubmitting ||
                    !form.formState.isValid ||
                    !isCustomAmountValid
                }
                onClickBack={handleClickBack}
                onSubmit={form.handleSubmit(handleSubmit)}
                paymentOptions={
                    <InvoicePaymentOptions
                        customAmount={customAmount}
                        paymentAmountType={paymentAmountType}
                        setCustomAmount={setCustomAmount}
                        setPaymentAmountType={setPaymentAmountType}
                    />
                }
                paymentSummary={
                    <InvoicePaymentSummary
                        customAmount={customAmount}
                        paymentAmountType={paymentAmountType}
                        showTipSection={showTipSection}
                    />
                }
                tipSection={
                    showTipSection ? (
                        <Controller<FormFields, 'tipAmount'>
                            name="tipAmount"
                            render={({ field }) => (
                                <InvoiceTipSection
                                    value={field.value ?? 0}
                                    invoice={invoice}
                                    totalPaymentAmount={totalPaymentAmount}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    ) : null
                }
                submitButtonText={t('payInvoice')}
            />
        </FormProvider>
    );
};

const StripeInvoicePaymentForm = ({
    customAmount,
    paymentAmountType,
    setCustomAmount,
    setPaymentAmountType
}: Props) => {
    const { staticUrl } = useBootstrapData();
    const { invoice } = useOutletContext<{ invoice: SpApi.Client.IInvoice }>();
    const theme = useTheme() as Theme;

    const stripeInstance = useMemo(() => {
        if (!invoice.stripePublicKey) {
            return null;
        }

        return loadStripe(invoice.stripePublicKey, {
            apiVersion: '2023-10-16',
            stripeAccount: invoice.stripeStandardAccountId ?? undefined
        });
    }, [invoice]);

    const stripeOptions = useMemo(() => {
        const paymentAmount =
            paymentAmountType === 'full'
                ? getFullPaymentAmount(invoice)
                : paymentAmountType === 'retainer'
                ? getPartialPaymentAmount(invoice)
                : Math.max(customAmount, 1);

        const stripePaymentAmount = Math.round(invoice.currencyMultiplier * paymentAmount);

        const options: StripeElementsOptions = {
            amount: stripePaymentAmount,
            appearance: getStripeAppearance(theme),
            currency: invoice.currencyCode.toLowerCase(),
            fonts: getStripeFonts(staticUrl),
            mode: 'payment',
            paymentMethodCreation: 'manual'
        };

        return options;
    }, [customAmount, invoice, paymentAmountType, staticUrl, theme]);

    return (
        <Elements options={stripeOptions} stripe={stripeInstance}>
            <StripeInvoicePaymentFormContent
                customAmount={customAmount}
                paymentAmountType={paymentAmountType}
                setCustomAmount={setCustomAmount}
                setPaymentAmountType={setPaymentAmountType}
            />
        </Elements>
    );
};

export default StripeInvoicePaymentForm;
